/**
 * Cascade binding rename across entire project.
 * When a datasource response field is renamed, all #{...oldPath...} bindings
 * must update to reference the new path.
 *
 * Strategy: JSON.stringify the project, regex-replace all #{oldPath.} and #{oldPath}
 * patterns with the new path, JSON.parse back. This catches ALL string occurrences
 * including flow params, UI text bindings, element attributes, etc.
 *
 * Boundary safety (per Research Pitfall 2): The regex matches #{EXACT_OLD_PATH} followed
 * by either `.` (sub-path continues) or `}` (end of binding). This prevents partial
 * matches like #{productList} when renaming #{product}.
 *
 * Per D-10, D-12, D-13.
 */

export interface CascadeResult {
  count: number
  updatedBlocks: Record<string, unknown>
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function cascadeBindingRename(
  blocks: Record<string, unknown>,
  oldPath: string,
  newPath: string,
): CascadeResult {
  if (oldPath === newPath) return { count: 0, updatedBlocks: blocks }

  const json = JSON.stringify(blocks)

  // Match #{oldPath} or #{oldPath.subfield...}
  // Boundary: must be followed by . or } to prevent partial prefix matches
  const escaped = escapeRegex(oldPath)
  const regex = new RegExp(`#\\{${escaped}([.}])`, 'g')

  const matches = json.match(regex)
  const count = matches?.length ?? 0

  if (count === 0) return { count: 0, updatedBlocks: blocks }

  const updated = json.replace(regex, `#{${newPath}$1`)
  const updatedBlocks = JSON.parse(updated) as Record<string, unknown>

  return { count, updatedBlocks }
}
